import { useApp } from '../../context/AppContext';
import { SliderInput } from './SliderInput';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

export const CreditInputs = () => {
  const { inputs, updateInput, t, currency, language } = useApp();
  const currencySuffix = currency === 'EUR' ? ' EUR' : ' RON';
  const monthLabel = language === 'ro' ? ' luni' : ' months';

  return (
    <div className="space-y-6">
      {/* Loan amount */}
      <SliderInput
        label={t('loanAmount')}
        value={inputs.loanAmount ?? 200000}
        onChange={(v) => updateInput('loanAmount', v)}
        min={5000} max={2000000} step={5000}
        suffix={currencySuffix}
        testId="loan-amount"
      />

      {/* Interest rate */}
      <SliderInput
        label={language === 'ro' ? 'Rata dobânzii anuale (%)' : 'Annual interest rate (%)'}
        value={inputs.creditRate ?? 5}
        onChange={(v) => updateInput('creditRate', v)}
        min={1} max={20} step={0.1}
        suffix="%"
        testId="credit-rate"
      />

      {/* Term */}
      <SliderInput
        label={language === 'ro' ? 'Perioadă rambursare' : 'Repayment period'}
        value={inputs.creditTermMonths ?? 300}
        onChange={(v) => updateInput('creditTermMonths', v)}
        min={6} max={480} step={6}
        suffix={monthLabel}
        testId="credit-term"
      />

      {/* Repayment type - clean select */}
      <div className="space-y-2">
        <Label className="text-xs uppercase tracking-[0.15em] text-foreground/70 font-semibold">
          {language === 'ro' ? 'Mod de rambursare' : 'Repayment type'}
        </Label>
        <div className="grid grid-cols-2 gap-2">
          {[
            { value: 'annuity', label: language === 'ro' ? '📊 Rate egale' : '📊 Equal payments' },
            { value: 'principal', label: language === 'ro' ? '📉 Principal constant' : '📉 Equal principal' },
          ].map(opt => (
            <button
              key={opt.value}
              onClick={() => updateInput('repaymentType', opt.value)}
              className={`px-3 py-2.5 rounded-xl text-sm font-medium border-2 transition-all duration-200 text-left
                ${(inputs.repaymentType ?? 'annuity') === opt.value
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300'
                  : 'border-border bg-background text-foreground/70 hover:border-blue-300'}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Extra payment */}
      <SliderInput
        label={language === 'ro' ? 'Plată anticipată lunară suplimentară' : 'Extra monthly payment'}
        value={inputs.extraMonthlyPayment ?? 0}
        onChange={(v) => updateInput('extraMonthlyPayment', v)}
        min={0} max={10000} step={50}
        suffix={currencySuffix}
        testId="extra-payment"
      />

      {/* Grace period */}
      <SliderInput
        label={language === 'ro' ? 'Perioadă de grație' : 'Grace period'}
        value={inputs.gracePeriodMonths ?? 0}
        onChange={(v) => updateInput('gracePeriodMonths', v)}
        min={0} max={24} step={1}
        suffix={monthLabel}
        testId="grace-period"
      />

      {/* Monthly fee - subtle inline */}
      <div className="flex items-center justify-between p-3 rounded-xl bg-muted/50 border border-border">
        <Label className="text-sm text-foreground/70">
          {language === 'ro' ? 'Comision lunar (% din credit)' : 'Monthly fee (% of loan)'}
        </Label>
        <div className="flex items-center gap-2">
          <input
            type="number"
            value={inputs.monthlyFeeRate ?? 0.03}
            onChange={(e) => updateInput('monthlyFeeRate', parseFloat(e.target.value) || 0)}
            className="w-20 text-right text-sm font-semibold bg-background border border-border rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
            step="0.01" min="0" max="2"
          />
          <span className="text-sm text-muted-foreground">%</span>
        </div>
      </div>
    </div>
  );
};

export default CreditInputs;
